import React, { useState } from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Heart, ShoppingCart, Package, Users, Handshake, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';
export function GetInvolvedPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showAcceptedItems, setShowAcceptedItems] = useState(false);
  const handleVolunteerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real implementation, this would submit the form data
    setFormSubmitted(true);
  };
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-959">
        <div className="text-center mb-12" data-id="element-960">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-961">
            Turn surplus into lifesaving care.
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-962">
            Your time, supplies, or sponsorship keeps small clinics in Egypt
            stocked and serving.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mt-8" data-id="element-963">
          <Button href="/donate" size="lg" data-id="element-964">
            Donate
          </Button>
          <Button href="https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share" variant="secondary" size="lg" className="flex items-center gap-2" onClick={e => {
          e.preventDefault();
          window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank');
        }} data-id="element-965">
            Shop Wish List <ExternalLink size={16} data-id="element-966" />
          </Button>
          <Button href="#volunteer-form" variant="secondary" size="lg" data-id="element-967">
            Volunteer
          </Button>
        </div>
      </Section>
      {/* Ways to Help Section */}
      <Section background="white" data-id="element-968">
        <div className="mb-12 text-center" data-id="element-969">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-970">
            Ways to Help
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-971">
            There are many ways to support our mission of bridging healthcare
            gaps for underserved communities.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-972">
          {/* Card 1: Donate Funds */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-973">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-974">
              <Heart size={24} className="text-white" data-id="element-975" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-976">
              Donate Funds
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-977">
              Your monthly gifts help cover shipping, customs, and operational
              costs to ensure medical supplies reach those who need them most.
            </p>
            <Button href="/donate" className="w-full" data-id="element-978">
              Donate
            </Button>
          </div>
          {/* Card 2: Shop Amazon Wish List */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-979">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-980">
              <ShoppingCart size={24} className="text-white" data-id="element-981" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-982">
              Shop Amazon Wish List
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-983">
              Purchase most-needed items that ship directly to us. Please
              include a gift message with your email so we can send an
              acknowledgment.
            </p>
            <Button className="w-full flex items-center justify-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-984">
              Open Wish List <ExternalLink size={16} data-id="element-985" />
            </Button>
          </div>
          {/* Card 3: Donate Medical Supplies */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-986">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-987">
              <Package size={24} className="text-white" data-id="element-988" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-989">
              Donate Medical Supplies
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-990">
              We accept unopened medical supplies within expiration date. All
              donations are carefully inspected and sorted before shipping.
            </p>
            <Button variant="secondary" className="w-full" onClick={() => setShowAcceptedItems(true)} data-id="element-991">
              See accepted items
            </Button>
          </div>
          {/* Card 4: Volunteer */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md" data-id="element-992">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-993">
              <Users size={24} className="text-white" data-id="element-994" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-995">
              Volunteer
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-996">
              Help with sorting, inventory management, packing, organizing
              drives, or community outreach. Every hour makes a difference.
            </p>
            <Button href="#volunteer-form" variant="secondary" className="w-full" data-id="element-997">
              Sign up to volunteer
            </Button>
          </div>
          {/* Card 5: Partnerships */}
          <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md md:col-span-2 lg:col-span-1" data-id="element-998">
            <div className="bg-primary-royal-blue rounded-full p-3 inline-block mb-4" data-id="element-999">
              <Handshake size={24} className="text-white" data-id="element-1000" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1001">
              Partnerships
            </h3>
            <p className="font-inter text-neutral-charcoal mb-6" data-id="element-1002">
              Hospitals, clinics, manufacturers, and businesses can partner with
              us through supply donations, corporate social responsibility
              programs, or sponsorships.
            </p>
            <Button href="/partners" variant="secondary" className="w-full" data-id="element-1003">
              Become a partner
            </Button>
          </div>
        </div>
      </Section>
      {/* Impact Snapshot Section */}
      <Section background="sand" data-id="element-1004">
        <div className="text-center mb-8" data-id="element-1005">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1006">
            Impact Snapshot
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal" data-id="element-1007">
            Your support makes a real difference in the lives of underserved
            communities.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 max-w-4xl mx-auto mb-6" data-id="element-1008">
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1009">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1010">
              2,500+
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1011">
              Pounds of supplies diverted
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1012">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1013">
              15
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1014">
              Clinics supported
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1015">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1016">
              750+
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1017">
              Volunteer hours
            </div>
          </div>
        </div>
        <div className="text-center text-sm font-inter text-neutral-charcoal/70" data-id="element-1018">
          Updated quarterly. Last update: June 2023
        </div>
      </Section>
      {/* How It Works Section */}
      <Section background="white" data-id="element-1019">
        <div className="text-center mb-12" data-id="element-1020">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1021">
            How It Works
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1022">
            Our streamlined process ensures medical supplies reach those who
            need them most efficiently and effectively.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1023">
          {/* Step 1 */}
          <div className="relative" data-id="element-1024">
            <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md h-full" data-id="element-1025">
              <div className="bg-primary-royal-blue text-white w-10 h-10 rounded-full flex items-center justify-center font-playfair font-bold text-xl mb-4" data-id="element-1026">
                1
              </div>
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1027">
                Collect
              </h3>
              <p className="font-inter text-neutral-charcoal" data-id="element-1028">
                We collect unused medical supplies from hospitals, clinics, and
                individual donors that would otherwise go to waste.
              </p>
            </div>
            <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10" data-id="element-1029">
              <ArrowRight size={24} className="text-primary-royal-blue" data-id="element-1030" />
            </div>
          </div>
          {/* Step 2 */}
          <div className="relative" data-id="element-1031">
            <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md h-full" data-id="element-1032">
              <div className="bg-primary-royal-blue text-white w-10 h-10 rounded-full flex items-center justify-center font-playfair font-bold text-xl mb-4" data-id="element-1033">
                2
              </div>
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1034">
                Prepare
              </h3>
              <p className="font-inter text-neutral-charcoal" data-id="element-1035">
                Volunteers sort, inspect, and inventory all supplies, ensuring
                they meet quality standards and match recipient needs.
              </p>
            </div>
            <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10" data-id="element-1036">
              <ArrowRight size={24} className="text-primary-royal-blue" data-id="element-1037" />
            </div>
          </div>
          {/* Step 3 */}
          <div data-id="element-1038">
            <div className="bg-neutral-offwhite rounded-xl p-6 shadow-md h-full" data-id="element-1039">
              <div className="bg-primary-royal-blue text-white w-10 h-10 rounded-full flex items-center justify-center font-playfair font-bold text-xl mb-4" data-id="element-1040">
                3
              </div>
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1041">
                Deliver
              </h3>
              <p className="font-inter text-neutral-charcoal" data-id="element-1042">
                Supplies are carefully packaged and shipped to Coptic monastery
                clinics in Egypt, where they immediately help patients in need.
              </p>
            </div>
          </div>
        </div>
        <div className="text-center mt-8" data-id="element-1043">
          <Button href="/how-it-works" variant="tertiary" data-id="element-1044">
            Learn more about our process
          </Button>
        </div>
      </Section>
      {/* Volunteer Form Section */}
      <Section id="volunteer-form" background="offwhite" data-id="element-1045">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1046">
          <div data-id="element-1047">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1048">
              Volunteer With Us
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1049">
              Join our team of dedicated volunteers who help sort, pack, and
              distribute medical supplies. Whether you have a few hours or can
              commit regularly, your contribution makes a difference.
            </p>
            <div className="bg-white p-6 rounded-xl shadow-md mb-6" data-id="element-1050">
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1051">
                Volunteer Opportunities
              </h3>
              <ul className="space-y-2 font-inter text-neutral-charcoal" data-id="element-1052">
                <li className="flex items-start" data-id="element-1053">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1054" />
                  <span data-id="element-1055">Sorting and inventory management</span>
                </li>
                <li className="flex items-start" data-id="element-1056">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1057" />
                  <span data-id="element-1058">Packing and preparing shipments</span>
                </li>
                <li className="flex items-start" data-id="element-1059">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1060" />
                  <span data-id="element-1061">Organizing and hosting supply drives</span>
                </li>
                <li className="flex items-start" data-id="element-1062">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1063" />
                  <span data-id="element-1064">Community outreach and education</span>
                </li>
                <li className="flex items-start" data-id="element-1065">
                  <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1066" />
                  <span data-id="element-1067">Administrative and technical support</span>
                </li>
              </ul>
            </div>
          </div>
          <div data-id="element-1068">
            {formSubmitted ? <div className="bg-white p-8 rounded-xl shadow-md text-center" data-id="element-1069">
                <CheckCircle size={48} className="text-primary-royal-blue mx-auto mb-4" data-id="element-1070" />
                <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue mb-3" data-id="element-1071">
                  Thank You!
                </h3>
                <p className="font-inter text-neutral-charcoal mb-4" data-id="element-1072">
                  Your volunteer application has been submitted successfully.
                  We'll be in touch with you shortly to discuss how you can get
                  involved.
                </p>
                <Button onClick={() => setFormSubmitted(false)} variant="secondary" data-id="element-1073">
                  Submit another application
                </Button>
              </div> : <form onSubmit={handleVolunteerSubmit} className="bg-white p-6 rounded-xl shadow-md" data-id="element-1074">
                <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1075">
                  Volunteer Application
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1076">
                  <div data-id="element-1077">
                    <label htmlFor="name" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1078">
                      Full Name*
                    </label>
                    <input type="text" id="name" name="name" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1079" />
                  </div>
                  <div data-id="element-1080">
                    <label htmlFor="email" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1081">
                      Email Address*
                    </label>
                    <input type="email" id="email" name="email" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1082" />
                  </div>
                </div>
                <div className="mb-4" data-id="element-1083">
                  <label htmlFor="city" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1084">
                    City/State*
                  </label>
                  <input type="text" id="city" name="city" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1085" />
                </div>
                <div className="mb-4" data-id="element-1086">
                  <label htmlFor="skills" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1087">
                    Skills/Interests*
                  </label>
                  <select id="skills" name="skills" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1088">
                    <option value="" data-id="element-1089">Select an option</option>
                    <option value="sorting" data-id="element-1090">Sorting & Inventory</option>
                    <option value="packing" data-id="element-1091">Packing & Shipping</option>
                    <option value="drives" data-id="element-1092">Organizing Drives</option>
                    <option value="outreach" data-id="element-1093">Community Outreach</option>
                    <option value="admin" data-id="element-1094">Administrative Support</option>
                    <option value="technical" data-id="element-1095">Technical Support</option>
                    <option value="other" data-id="element-1096">Other</option>
                  </select>
                </div>
                <div className="mb-4" data-id="element-1097">
                  <label htmlFor="availability" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1098">
                    Availability*
                  </label>
                  <select id="availability" name="availability" required className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" data-id="element-1099">
                    <option value="" data-id="element-1100">Select an option</option>
                    <option value="weekdays" data-id="element-1101">Weekdays</option>
                    <option value="evenings" data-id="element-1102">Evenings</option>
                    <option value="weekends" data-id="element-1103">Weekends</option>
                    <option value="flexible" data-id="element-1104">Flexible</option>
                    <option value="one-time" data-id="element-1105">One-time event</option>
                  </select>
                </div>
                <div className="mb-4" data-id="element-1106">
                  <label htmlFor="message" className="block font-inter text-neutral-charcoal mb-1" data-id="element-1107">
                    Message (Optional)
                  </label>
                  <textarea id="message" name="message" rows={4} className="w-full px-3 py-2 border border-neutral-charcoal/20 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-royal-blue" placeholder="Tell us a bit more about yourself and why you're interested in volunteering." data-id="element-1108"></textarea>
                </div>
                <div className="mb-6" data-id="element-1109">
                  <label className="flex items-start" data-id="element-1110">
                    <input type="checkbox" required className="mt-1 mr-2" data-id="element-1111" />
                    <span className="font-inter text-sm text-neutral-charcoal" data-id="element-1112">
                      I consent to receiving emails about volunteer
                      opportunities. I understand I can unsubscribe at any time.
                    </span>
                  </label>
                </div>
                <Button type="submit" className="w-full" data-id="element-1113">
                  Submit Application
                </Button>
              </form>}
          </div>
        </div>
      </Section>
      {/* Partner Mini-Section */}
      <Section background="white" data-id="element-1114">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1115">
          <div data-id="element-1116">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1117">
              Partner With Us
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1118">
              Hospitals, clinics, manufacturers, and businesses can partner with
              Farasha Medical Aid to make a significant impact on healthcare
              access for underserved communities.
            </p>
            <div className="space-y-4 mb-6" data-id="element-1119">
              <div className="flex items-start" data-id="element-1120">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1121" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1122">
                  Supply donation partnerships for hospitals and clinics
                </span>
              </div>
              <div className="flex items-start" data-id="element-1123">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1124" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1125">
                  Corporate social responsibility programs
                </span>
              </div>
              <div className="flex items-start" data-id="element-1126">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1127" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1128">
                  Sponsorships for shipping and operational costs
                </span>
              </div>
              <div className="flex items-start" data-id="element-1129">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1130" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1131">
                  In-kind donations of services and expertise
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4" data-id="element-1132">
              <Button href="/partners" data-id="element-1133">Request a partnership kit</Button>
              <Button variant="secondary" href="/downloads/host-a-drive-toolkit.pdf" data-id="element-1134">
                Download Drive Toolkit (PDF)
              </Button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1135">
            <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Business team discussing partnership opportunities" className="w-full h-full object-cover" data-id="element-1136" />
          </div>
        </div>
      </Section>
      {/* FAQ Teaser */}
      <Section background="sand" data-id="element-1137">
        <div className="text-center" data-id="element-1138">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1139">
            Have Questions?
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal mb-8 max-w-3xl mx-auto" data-id="element-1140">
            Find answers to common questions about donations, volunteering, and
            our work in our frequently asked questions section.
          </p>
          <Button href="/faq" variant="secondary" data-id="element-1141">
            Visit FAQ Page
          </Button>
        </div>
      </Section>
      {/* Secondary CTA Band */}
      <Section background="deep-blue" data-id="element-1142">
        <div className="text-center" data-id="element-1143">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-1144">
            Every $25 ships a box of care.
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-1145">
            Your support helps us deliver critical medical supplies to
            underserved communities in Egypt.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1146">
            <Button href="/donate" size="lg" data-id="element-1147">
              Donate
            </Button>
            <Button variant="secondary" size="lg" className="flex items-center gap-2" onClick={() => window.open('https://www.amazon.com/hz/wishlist/ls/2BKAGMEAOQZHZ?ref_=wl_share', '_blank')} data-id="element-1148">
              Shop Wish List <ExternalLink size={16} data-id="element-1149" />
            </Button>
          </div>
        </div>
      </Section>
      {/* Accepted Items Modal */}
      {showAcceptedItems && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1150">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" data-id="element-1151">
            <div className="flex justify-between items-start mb-4" data-id="element-1152">
              <h3 className="text-2xl font-playfair font-bold text-primary-deep-blue" data-id="element-1153">
                Accepted Medical Supplies
              </h3>
              <button onClick={() => setShowAcceptedItems(false)} className="text-neutral-charcoal hover:text-primary-royal-blue" data-id="element-1154">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1155">
                  <line x1="18" y1="6" x2="6" y2="18" data-id="element-1156"></line>
                  <line x1="6" y1="6" x2="18" y2="18" data-id="element-1157"></line>
                </svg>
              </button>
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1158">
              <p className="mb-4" data-id="element-1159">
                We accept the following items, provided they are:
              </p>
              <ul className="list-disc pl-5 mb-4 space-y-1" data-id="element-1160">
                <li data-id="element-1161">Unopened and sealed in original packaging</li>
                <li data-id="element-1162">Not expired or within 6 months of expiration</li>
                <li data-id="element-1163">Clean and in good condition</li>
              </ul>
              <h4 className="font-bold mb-2 mt-4" data-id="element-1164">Accepted Items:</h4>
              <div className="grid md:grid-cols-2 gap-x-6 gap-y-2" data-id="element-1165">
                <div data-id="element-1166">
                  <h5 className="font-bold text-primary-royal-blue mt-2" data-id="element-1167">
                    Wound Care
                  </h5>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-1168">
                    <li data-id="element-1169">Bandages and gauze</li>
                    <li data-id="element-1170">Adhesive tape</li>
                    <li data-id="element-1171">Antiseptic wipes</li>
                    <li data-id="element-1172">Sutures</li>
                    <li data-id="element-1173">Wound dressings</li>
                  </ul>
                </div>
                <div data-id="element-1174">
                  <h5 className="font-bold text-primary-royal-blue mt-2" data-id="element-1175">
                    Personal Protective Equipment
                  </h5>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-1176">
                    <li data-id="element-1177">Masks (surgical, N95)</li>
                    <li data-id="element-1178">Gloves (latex, nitrile)</li>
                    <li data-id="element-1179">Gowns</li>
                    <li data-id="element-1180">Face shields</li>
                    <li data-id="element-1181">Shoe covers</li>
                  </ul>
                </div>
                <div data-id="element-1182">
                  <h5 className="font-bold text-primary-royal-blue mt-2" data-id="element-1183">
                    Diagnostic Equipment
                  </h5>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-1184">
                    <li data-id="element-1185">Stethoscopes</li>
                    <li data-id="element-1186">Blood pressure cuffs</li>
                    <li data-id="element-1187">Otoscopes</li>
                    <li data-id="element-1188">Thermometers</li>
                    <li data-id="element-1189">Pulse oximeters</li>
                  </ul>
                </div>
                <div data-id="element-1190">
                  <h5 className="font-bold text-primary-royal-blue mt-2" data-id="element-1191">
                    Medications
                  </h5>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-1192">
                    <li data-id="element-1193">Over-the-counter medications</li>
                    <li data-id="element-1194">Antibiotics</li>
                    <li data-id="element-1195">Pain relievers</li>
                    <li data-id="element-1196">Antifungals</li>
                    <li data-id="element-1197">Antihistamines</li>
                  </ul>
                </div>
                <div data-id="element-1198">
                  <h5 className="font-bold text-primary-royal-blue mt-2" data-id="element-1199">
                    Dental Supplies
                  </h5>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-1200">
                    <li data-id="element-1201">Dental instruments</li>
                    <li data-id="element-1202">Filling materials</li>
                    <li data-id="element-1203">Dental anesthetics</li>
                    <li data-id="element-1204">Sterilization supplies</li>
                  </ul>
                </div>
                <div data-id="element-1205">
                  <h5 className="font-bold text-primary-royal-blue mt-2" data-id="element-1206">
                    Other Supplies
                  </h5>
                  <ul className="list-disc pl-5 space-y-1" data-id="element-1207">
                    <li data-id="element-1208">IV supplies</li>
                    <li data-id="element-1209">Syringes and needles</li>
                    <li data-id="element-1210">Surgical instruments</li>
                    <li data-id="element-1211">Sterilization equipment</li>
                    <li data-id="element-1212">Medical textbooks (recent editions)</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-neutral-offwhite rounded-lg" data-id="element-1213">
                <h4 className="font-bold mb-2" data-id="element-1214">Important Notes:</h4>
                <ul className="list-disc pl-5 space-y-1" data-id="element-1215">
                  <li data-id="element-1216">
                    We cannot accept opened packages, expired items, or used
                    equipment
                  </li>
                  <li data-id="element-1217">
                    All donations are subject to inspection and may be declined
                    if they don't meet our standards
                  </li>
                  <li data-id="element-1218">
                    For large donations, please contact us in advance at{' '}
                    <a href="mailto:farashamedicalaid@gmail.com" className="text-primary-royal-blue hover:underline" data-id="element-1219">
                      farashamedicalaid@gmail.com
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-6" data-id="element-1220">
              <Button onClick={() => setShowAcceptedItems(false)} className="w-full" data-id="element-1221">
                Close
              </Button>
            </div>
          </div>
        </div>}
    </>;
}